import { emitGit } from '@/websocket/wsClient'

// ── Shared types ───────────────────────────────────────────────────────────────
// Mirror the simple-git shapes so consumers don't depend on the library directly.

export interface GitFile {
  path:        string
  index:       string   // staged status character (M, A, D, ?, …)
  working_dir: string   // working-tree status character
}

export interface GitStatusResult {
  current:  string
  tracking: string | null
  files:    GitFile[]
  staged:   GitFile[]   // files where index !== ' ' and index !== '?'
  unstaged: GitFile[]   // files where working_dir !== ' '
}

export interface GitCommit {
  hash:        string
  message:     string
  author_name: string
  author_email: string
  date:        string
}

export interface GitLogResult {
  all:   GitCommit[]
  total: number
  latest: GitCommit | null
}

export interface GitBranchSummary {
  current:  string
  all:      string[]
  branches: Record<string, { current: boolean; name: string; commit: string; label: string }>
}

// ── Status ─────────────────────────────────────────────────────────────────────

/**
 * Fetch the current working-tree status for a repository.
 * Augments the raw simple-git StatusResult with pre-split staged/unstaged lists.
 */
export async function getGitStatus(repoPath: string): Promise<GitStatusResult> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = await emitGit<any>('git:status', { repoPath })
  const files: GitFile[] = raw.files ?? []
  return {
    current:  raw.current  ?? '',
    tracking: raw.tracking ?? null,
    files,
    staged:   files.filter(f => f.index   !== ' ' && f.index   !== '?' && f.index   !== ''),
    unstaged: files.filter(f => f.working_dir !== ' ' && f.working_dir !== ''),
  }
}

// ── Branches ───────────────────────────────────────────────────────────────────

/** Fetch all local branches for the given repository. */
export async function getGitBranches(repoPath: string): Promise<GitBranchSummary> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = await emitGit<any>('git:branches', { repoPath })
  return {
    current:  raw.current  ?? '',
    all:      raw.all      ?? [],
    branches: raw.branches ?? {},
  }
}

// ── Log ────────────────────────────────────────────────────────────────────────

/** Fetch the last N commits from HEAD. */
export async function getGitLog(repoPath: string, limit = 50): Promise<GitLogResult> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = await emitGit<any>('git:log', { repoPath, limit })
  return {
    all:    raw.all    ?? [],
    total:  raw.total  ?? 0,
    latest: raw.latest ?? null,
  }
}

// ── Diff ───────────────────────────────────────────────────────────────────────

/** Return the unified diff for a single file (or the full working tree). */
export async function getGitDiff(repoPath: string, filePath?: string): Promise<string> {
  return emitGit<string>('git:diff', { repoPath, filePath: filePath ?? '' })
}

/**
 * Fetch the HEAD version of a file for the Monaco DiffEditor.
 * Returns an empty string for new (untracked) files that have no HEAD entry.
 */
export async function getFileAtHead(repoPath: string, filePath: string): Promise<string> {
  return emitGit<string>('git:fileAtHead', { repoPath, filePath })
}

// ── Staging ────────────────────────────────────────────────────────────────────

/** Stage a single file. */
export async function stageFile(repoPath: string, filePath: string): Promise<void> {
  await emitGit<void>('git:stageFile', { repoPath, filePath })
}

/** Unstage a single file. */
export async function unstageFile(repoPath: string, filePath: string): Promise<void> {
  await emitGit<void>('git:unstageFile', { repoPath, filePath })
}

/** Stage all changed files. */
export async function stageAll(repoPath: string): Promise<void> {
  await emitGit<void>('git:stageAll', { repoPath })
}

/** Unstage all staged files (reset index to HEAD). */
export async function unstageAll(repoPath: string): Promise<void> {
  await emitGit<void>('git:unstageAll', { repoPath })
}

// ── Commit ─────────────────────────────────────────────────────────────────────

/** Commit all staged changes with the given message. */
export async function commitChanges(repoPath: string, message: string, authorName: string, authorEmail: string): Promise<void> {
  await emitGit<void>('git:commit', { repoPath, message, authorName, authorEmail })
}

// ── Branch management ──────────────────────────────────────────────────────────

/** Switch to an existing branch. */
export async function checkoutBranch(repoPath: string, branch: string): Promise<void> {
  await emitGit<void>('git:checkout', { repoPath, branch })
}

/** Create and switch to a new local branch. */
export async function createBranch(repoPath: string, name: string): Promise<void> {
  await emitGit<void>('git:createBranch', { repoPath, name })
}

// ── Remote sync ────────────────────────────────────────────────────────────────

/** Pull from the tracking remote branch. */
export async function pullChanges(repoPath: string, nasPassword?: string): Promise<void> {
  await emitGit<void>('git:pull', { repoPath, nasPassword })
}

/** Push to the tracking remote branch. */
export async function pushChanges(repoPath: string, nasPassword?: string): Promise<void> {
  await emitGit<void>('git:push', { repoPath, nasPassword })
}
